import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from 'antd';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAddressBook, faChevronRight, faMagnifyingGlass, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { useContacts } from '../../queries/contacts';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { usePhone } from '../../hooks/usePhone';
import { onPhone } from '../../theme/tokens';
import { formatPhone } from '../../utils/callLog';
import { CONTACT_VIEWS, profilePath } from '../../utils/contacts';
import { AaTheme, Av, Btn, I, Seg, cx } from '../Todos/ui';
import { StatusChip, TypeChip } from './parts';
import Quick, { QuickText } from './Quick';
import AddContactModal from './AddContactModal';
import { scheduleInitial, useSchedule } from './useSchedule';
import '../Todos/aa.css';
import '../../styles/mcc-pages.css';

const Search = styled(Input)`
  ${onPhone} {
    .ant-input-clear-icon {
      margin: -12px;
      padding: 12px;
      font-size: 16px;
    }
  }
`;

export default function Contacts() {
  const phone = usePhone();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = CONTACT_VIEWS.some(([k]) => k === params.get('type')) ? params.get('type') : 'all';
  const [q, setQ] = useState('');
  const search = useDebouncedValue(q, 250);
  const [adding, setAdding] = useState(false);
  const [texting, setTexting] = useState(null);
  const [schedule, scheduleNode] = useSchedule();
  const { rows, counts, isLoading } = useContacts({ type: view === 'all' ? undefined : view, search });

  const setView = (v) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === 'all') next.delete('type');
        else next.set('type', v);
        return next;
      },
      { replace: true }
    );
  const open = (c) => navigate(profilePath(c));
  const quick = (c) => (
    <Quick contact={c} icons onText={() => setTexting(c)} onAction={(kind) => schedule(kind, scheduleInitial(kind, c))} />
  );

  const list = phone ? (
    <div className="cp-mlist">
      {rows.map((c) => (
        <button key={`${c.kind}-${c.id}`} type="button" className="cp-mrow" onClick={() => open(c)}>
          <Av name={c.name} cls="lt sm" />
          <div className="grow cp-mm">
            <div className="m-row gap4">
              <span className="b ell">{c.name}</span>
              <TypeChip type={c.type} />
            </div>
            <div className="m-sub ell">{c.home || (c.phone && formatPhone(c.phone))}</div>
            <div className="m-row gap4">
              <StatusChip contact={c} />
            </div>
          </div>
          <I icon={faChevronRight} />
        </button>
      ))}
    </div>
  ) : (
    <div className="cl3-tw">
      <table className="cl3-tbl cp-tbl">
        <thead>
          <tr>
            {['Name', 'Type', 'Phone', 'Property or unit', 'Status'].map((x) => (
              <th key={x}>{x}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={`${c.kind}-${c.id}`} tabIndex={0} onClick={() => open(c)} onKeyDown={(e) => e.key === 'Enter' && open(c)}>
              <td>
                <div className="cl3-ct">
                  <Av name={c.name} cls="lt sm" />
                  <span className="b">{c.name}</span>
                  <div className="qa7-hover">{quick(c)}</div>
                </div>
              </td>
              <td>
                <TypeChip type={c.type} />
              </td>
              <td className="num nowrap">{c.phone && formatPhone(c.phone)}</td>
              <td className="cl3-prop" style={{ maxWidth: 260 }} title={c.home}>
                {c.home || '--'}
              </td>
              <td>
                <StatusChip contact={c} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <AaTheme>
      <div className={cx('aa', phone && 'ph')}>
        <div className="m-page">
          <div className="m-ptitle">
            <div className="grow">
              <h1>Contacts</h1>
              <div className="m-sub">{counts.all || 0} people · tenants, prospects, applicants, owners and vendors</div>
            </div>
            <Btn kind="primary" sm icon={faUserPlus} onClick={() => setAdding(true)}>
              Add contact
            </Btn>
          </div>
          <div className="m-col gap8">
            <div className="aa-types">
              <Seg
                items={CONTACT_VIEWS.map(([k, label]) => [k, label, null, counts[k] || 0])}
                value={view}
                onChange={setView}
                label="Contact type"
              />
            </div>
            <Search
              className="aa-search"
              allowClear
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: '#8C8C8C' }} />}
              placeholder="Search name, phone or property"
              aria-label="Search contacts"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          {texting && <QuickText contact={texting} onClose={() => setTexting(null)} />}
          {rows.length ? (
            list
          ) : (
            <div className="m-emptyc">
              <div className="m-emi">
                <I icon={faAddressBook} />
              </div>
              <div className="b">{isLoading ? 'Loading contacts…' : 'No contacts match.'}</div>
            </div>
          )}
        </div>
        {adding && <AddContactModal phone={phone} onClose={() => setAdding(false)} />}
      </div>
      {scheduleNode}
    </AaTheme>
  );
}
